import type { UserWithCounts } from "../../../lib/api/admin.api";
import { useAdminUpdateRole, useAdminSuspendUser } from "../../../lib/api/admin.api";
import { formatRelativeDate, formatShortDate } from "../../../shared/helpers/format-date";
import { useState, useEffect } from "react";

interface AdminUserRowProps {
  readonly user: UserWithCounts;
  readonly currentUserId: string;
  readonly onResetPassword: (user: UserWithCounts) => void;
}

function AuthBadges({ user }: { readonly user: UserWithCounts }) {
  const methods: string[] = [];
  if (user.hasPassword) methods.push("Email");
  for (const acc of user.oauthAccounts) {
    methods.push(acc.provider === "google" ? "Google" : "GitHub");
  }
  if (methods.length === 0) methods.push("OAuth");

  return (
    <div className="flex gap-1">
      {methods.map((m) => (
        <span
          key={m}
          className="text-[9px] px-1 py-0.5 rounded bg-surface-alt text-ink-faint"
        >
          {m}
        </span>
      ))}
    </div>
  );
}

export function AdminUserRow({ user, currentUserId, onResetPassword }: AdminUserRowProps) {
  const updateRole = useAdminUpdateRole();
  const suspendUser = useAdminSuspendUser();
  const [isMobile, setIsMobile] = useState(false);
  const isSelf = user.id === currentUserId;
  const isSuspended = !!user.suspendedAt;

  // Détecter mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const toggleRole = () => {
    const newRole = user.role === "ADMIN" ? "USER" : "ADMIN";
    updateRole.mutate({ userId: user.id, role: newRole });
  };

  const toggleSuspend = () => {
    suspendUser.mutate({ userId: user.id });
  };

  return (
    <div className={`flex ${isMobile ? "flex-col" : "items-center"} gap-3 p-3 bg-surface-alt rounded-xl ${isSuspended ? "opacity-50" : ""}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-ink truncate">{user.name}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
            user.role === "ADMIN" ? "bg-accent/15 text-accent" : "bg-border text-ink-muted"
          }`}>
            {user.role}
          </span>
          {isSelf && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-green-500/15 text-green-500">
              Vous
            </span>
          )}
          {isSuspended && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-red-500/15 text-red-500">
              Suspendu
            </span>
          )}
        </div>
        <span className="text-xs text-ink-muted block">{user.email}</span>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <AuthBadges user={user} />
          <div className="flex gap-3 text-[10px] text-ink-muted">
            <span>{user._count.tasks} tâches</span>
            <span>{user._count.events} événements</span>
            <span>{user._count.habits} habitudes</span>
          </div>
        </div>
        <div className={`flex ${isMobile ? "flex-col" : "gap-3"} mt-0.5 text-[10px] text-ink-faint`}>
          <span>Inscrit {formatShortDate(user.createdAt)}</span>
          <span>
            {user.lastActiveAt
              ? `Actif ${formatRelativeDate(new Date(user.lastActiveAt))}`
              : "Jamais actif"}
          </span>
        </div>
      </div>
      <div className={`flex ${isMobile ? "w-full" : ""} gap-1.5 shrink-0 ${isMobile ? "flex-wrap" : "flex-wrap"}`}>
        {!isSelf && (
          <>
            <button
              type="button"
              onClick={toggleRole}
              disabled={updateRole.isPending}
              className="px-2 py-1 text-[11px] bg-surface border border-border rounded
                hover:bg-surface-alt text-ink-muted hover:text-ink disabled:opacity-40"
            >
              {user.role === "ADMIN" ? "Retirer admin" : "Promouvoir"}
            </button>
            <button
              type="button"
              onClick={toggleSuspend}
              disabled={suspendUser.isPending}
              className={`px-2 py-1 text-[11px] bg-surface border rounded disabled:opacity-40 ${
                isSuspended
                  ? "border-green-500/30 text-green-500 hover:bg-green-500/5"
                  : "border-red-500/30 text-red-500 hover:bg-red-500/5"
              }`}
            >
              {isSuspended ? "Réactiver" : "Suspendre"}
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => onResetPassword(user)}
          className="px-2 py-1 text-[11px] bg-surface border border-border rounded
            hover:bg-surface-alt text-ink-muted hover:text-ink"
        >
          Reset mdp
        </button>
      </div>
    </div>
  );
}
